// Stronger normalization (unchanged)
export const normalizeText = (text: string) => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Levenshtein (unchanged)
const levenshtein = (a: string, b: string) => {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = new Array(n + 1);
  for (let j = 0; j <= n; j++) dp[j] = j;
  for (let i = 1; i <= m; i++) {
    let prev = dp[0];
    dp[0] = i;
    for (let j = 1; j <= n; j++) {
      const temp = dp[j];
      dp[j] = a[i - 1] === b[j - 1] ? prev : Math.min(prev + 1, dp[j] + 1, dp[j - 1] + 1);
      prev = temp;
    }
  }
  return dp[n];
};

export const fuzzyThreshold = (len: number) => {
  if (len <= 4) return 1;
  if (len <= 7) return 2;
  return 3;
};

// NEW: windowed distance between needle and any same-length slice of token.
// Returns best distance and whether it hit the start-of-word (small bonus).
const windowedDistance = (token: string, needle: string) => {
  const L = needle.length;
  if (L === 0) return { d: 0, startBonus: false };
  if (token.length < L) {
    // fall back to whole-token vs needle if token is shorter
    return { d: levenshtein(token, needle), startBonus: true };
  }
  let best = Number.POSITIVE_INFINITY;
  let startBonus = false;
  for (let i = 0; i <= token.length - L; i++) {
    const win = token.slice(i, i + L);
    const d = levenshtein(win, needle);
    if (d < best) {
      best = d;
      startBonus = i === 0; // prefer prefix windows
    }
    if (best === 0) break;
  }
  return { d: best, startBonus };
};

// Length-aware fuzzy with word windows.
// Returns { matched, distance } where lower distance ranks higher.
export const fuzzyCheck = (hay: string, needle: string) => {
  if (!needle) return { matched: true, distance: 0 };

  // exact match on full string
  if (hay === needle) return { matched: true, distance: 0 };

  const tokens = hay.split(/[\s-]+/).filter(Boolean);
  const th = fuzzyThreshold(needle.length);

  // 1) word-aware fast paths (ranked)
  for (const t of tokens) {
    if (t === needle) return { matched: true, distance: 0 };
  }
  for (const t of tokens) {
    if (t.startsWith(needle)) return { matched: true, distance: 0.25 }; // strong prefix
  }
  for (const t of tokens) {
    if (t.includes(needle)) return { matched: true, distance: 0.5 }; // in-word partial
  }
  // global includes across spaces/hyphens (weaker)
  if (hay.includes(needle)) return { matched: true, distance: 0.75 };

  // 2) sliding-window fuzzy on each token (same-length windows)
  let bestScore = Number.POSITIVE_INFINITY;
  for (const t of tokens) {
    const { d, startBonus } = windowedDistance(t, needle);
    if (d <= th) {
      // Score: base 1 + distance, with a small bonus for prefix windows
      const score = 1 + d - (startBonus ? 0.25 : 0);
      if (score < bestScore) bestScore = score;
    }
  }
  if (bestScore !== Number.POSITIVE_INFINITY) {
    return { matched: true, distance: bestScore };
  }

  // 3) fallback: fuzzy on compacted string (multi-word cases)
  const dFull = levenshtein(hay.replace(/\s+/g, ''), needle.replace(/\s+/g, ''));
  if (dFull <= th) return { matched: true, distance: 1 + dFull + 0.25 };

  return { matched: false, distance: Infinity };
};
